// Phase SIKA-2 — Goal-contextual message templates.
//
// Source: SIKA_Independence_Architecture_v1.0 §05.1 (Tables 14-18).
// Five template types — each returns a structured payload + body text
// that can be inserted into `sika_messages` with `template_id` set.
//
// Doctrine:
//   N06 — Every recalibration MUST emit a message. The recalibration
//         RPC writes both the cycle update AND a chat row using
//         postLossMessage / urgentModeMessage / similar.
//   N07 — Forbidden generic tipster vocab ("good form", "looking
//         strong", "in form") never appears in any template body.
//         Vitest asserts this against the verbatim doc strings.
//   Verbatim — copy from Tables 14-18 is reproduced as-is. The doc
//         is the canonical source. Substitution markers use [name].

export type SikaTemplateId =
  | "post_win"
  | "post_loss"
  | "milestone"
  | "urgent_mode"
  | "goal_reached";

export interface SikaTemplateMessage {
  template_id: SikaTemplateId;
  body: string;
  payload: Record<string, unknown>;
}

export interface PostWinInputs {
  cycle_number: number;
  goal_name: string;
  current_amount: number;
  target_amount: number;
  protected_this_week: number;
  banked: number;
  pace: "ahead" | "on" | "near";
  weeks_ahead_or_remaining: number;
}

function pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

function ghc(amount: number): string {
  return `GH₵${amount.toLocaleString("en-GH", { maximumFractionDigits: 0 })}`;
}

/** Table 14 — Post-Win (Goal Making Progress). */
export function postWinMessage(inputs: PostWinInputs): SikaTemplateMessage {
  const {
    cycle_number,
    goal_name,
    current_amount,
    target_amount,
    protected_this_week,
    banked,
    pace,
    weeks_ahead_or_remaining,
  } = inputs;
  const pctTxt = `${String(pct(current_amount, target_amount))}%`;
  const paceLine =
    pace === "ahead"
      ? `You are ${String(weeks_ahead_or_remaining)} weeks ahead of your original schedule at this rate.`
      : pace === "on"
        ? `On track. ${String(weeks_ahead_or_remaining)} weeks remaining at current pace.`
        : `Near goal. ${String(weeks_ahead_or_remaining)} weeks remaining.`;
  const body = `Week ${String(cycle_number)} complete. ${goal_name} update:

${ghc(current_amount)} of ${ghc(target_amount)} — ${pctTxt} to goal.
You protected ${ghc(protected_this_week)} this week.
Even if the chain ends now: ${ghc(banked)} secured toward ${goal_name}.

${paceLine}`;
  return {
    template_id: "post_win",
    body,
    payload: { cycle_number, goal_name, current_amount, target_amount, banked, pace },
  };
}

export interface PostLossInputs {
  cycle_number: number;
  goal_name: string;
  current_amount: number;
  target_amount: number;
  banked: number;
  weeks_remaining: number;
  new_target: number;
  prev_target: number;
  rationale: string;
}

/** Table 15 — Post-Loss (Recalibration). */
export function postLossMessage(inputs: PostLossInputs): SikaTemplateMessage {
  const {
    cycle_number,
    goal_name,
    current_amount,
    target_amount,
    banked,
    weeks_remaining,
    new_target,
    prev_target,
    rationale,
  } = inputs;
  const direction =
    new_target > prev_target ? "higher" : new_target < prev_target ? "lower" : "same";
  const body = `Week ${String(cycle_number)} didn't land. ${goal_name} plan update:

Current: ${ghc(current_amount)} of ${ghc(target_amount)}.
${ghc(banked)} is still secured — that did not change.

Plan recalibrated. With ${String(weeks_remaining)} weeks remaining,
I'm targeting ${new_target.toFixed(2)}× next cycle — ${direction} than this week.
Reason: ${rationale}

Losses happen. The plan adjusts. What matters is the next selection.`;
  return {
    template_id: "post_loss",
    body,
    payload: { cycle_number, goal_name, new_target, prev_target, weeks_remaining, rationale },
  };
}

export interface MilestoneInputs {
  banked: number;
  goal_name: string;
  remaining: number;
  weeks_remaining: number;
}

/** Table 16 — Milestone Reached (e.g. GH₵5,000 Banked). */
export function milestoneMessage(inputs: MilestoneInputs): SikaTemplateMessage {
  const { banked, goal_name, remaining, weeks_remaining } = inputs;
  const body = `${ghc(banked)} banked toward ${goal_name}.

This is a milestone. Even if every future selection loses,
${ghc(banked)} is secured toward your goal.
That is not luck. That is the compounding discipline working.

${ghc(remaining)} still to go. ${String(weeks_remaining)} weeks. The plan continues.`;
  return {
    template_id: "milestone",
    body,
    payload: { banked, goal_name, remaining, weeks_remaining },
  };
}

export interface UrgentModeInputs {
  weeks_remaining: number;
  pct_funded: number;
  target: number;
  projected_if_lands: number;
  new_pct_if_lands: number;
}

/** Table 17 — Timeline Pressure (Urgent Mode). */
export function urgentModeMessage(inputs: UrgentModeInputs): SikaTemplateMessage {
  const {
    weeks_remaining,
    pct_funded,
    target,
    projected_if_lands,
    new_pct_if_lands,
  } = inputs;
  const body = `${String(weeks_remaining)} weeks remaining. ${String(pct_funded)}% funded.

The plan needs adjustment. I'm targeting ${target.toFixed(2)}× this cycle
— higher than usual because the timeline requires it.
This carries more risk. I want you to know that before you stake.

If this lands: ${ghc(projected_if_lands)} — ${String(new_pct_if_lands)}% funded, back on pace.
If it doesn't: the plan recalibrates again. The goal is still reachable.`;
  return {
    template_id: "urgent_mode",
    body,
    payload: { weeks_remaining, pct_funded, target, projected_if_lands },
  };
}

export interface GoalReachedInputs {
  goal_name: string;
  target_amount: number;
  total_banked: number;
  weeks_to_completion: number;
  total_selections: number;
  won: number;
  lost: number;
}

/** Table 18 — Goal Reached. */
export function goalReachedMessage(inputs: GoalReachedInputs): SikaTemplateMessage {
  const {
    goal_name,
    target_amount,
    total_banked,
    weeks_to_completion,
    total_selections,
    won,
    lost,
  } = inputs;
  const body = `${goal_name} is funded. ${ghc(target_amount)} reached.

This is what the plan was for.

Protected along the way: ${ghc(total_banked)}.
Weeks to completion: ${String(weeks_to_completion)}.
Selections made: ${String(total_selections)}. Won: ${String(won)}. Lost: ${String(lost)}.

What do you want to build next?`;
  return {
    template_id: "goal_reached",
    body,
    payload: { goal_name, target_amount, total_banked, weeks_to_completion, won, lost },
  };
}

/** Phase SIKA-5 Layout — Per-leg "SIKA → ..." prefix.
 *  Doctrinal voice. Per the founder's video walkthrough, every leg's
 *  reasoning is presented as a direct SIKA quote. Idempotent — calling
 *  twice does not double-prefix. */
export function prefixSikaReasoning(text: string): string {
  const t = text.trim();
  if (!t) return "";
  // Already prefixed (allow either ASCII -> or unicode arrow)
  if (/^SIKA\s*(?:→|->)/i.test(t)) return t;
  return `SIKA → ${t}`;
}

/** N07 forbidden generic tipster vocab — must NEVER appear in any
 *  template body. Tested in messageTemplates.test.ts. */
export const SIKA_TEMPLATE_FORBIDDEN_VOCAB: readonly string[] = [
  "good form",
  "looking strong",
  "in form",
  "in good shape",
  "good shape",
  "hot streak",
  "value bet",
];
